import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import NoticeList from '../components/NoticeList';
import useLocalStorage from '../hooks/useLocalStorage';
import { REFRESH_INTERVAL, MANUAL_REFRESH_COOLDOWN } from '../constants';
import './NoticePage.css';

function NoticePage() {
  const [selectedCollege, setSelectedCollege] = useLocalStorage('selectedCollege', null);
  const [selectedDepartment, setSelectedDepartment] = useLocalStorage('selectedDepartment', null);
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [cooldown, setCooldown] = useState(0);
  const [activeTab, setActiveTab] = useState('global'); // 'global', 'college', 'department'
  const [autoRefreshTimer, setAutoRefreshTimer] = useState(REFRESH_INTERVAL);

  const handleRefresh = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshTime < MANUAL_REFRESH_COOLDOWN && refreshTrigger !== 0) {
      return; // Cooldown active
    }
    setRefreshTrigger(prev => prev + 1);
    setLastRefreshTime(now);
    setCooldown(MANUAL_REFRESH_COOLDOWN);
    setAutoRefreshTimer(REFRESH_INTERVAL); // Reset auto refresh timer on manual refresh
  }, [lastRefreshTime, refreshTrigger]);

  // Auto refresh timer
  useEffect(() => {
    const interval = setInterval(() => {
      setAutoRefreshTimer(prev => {
        if (prev <= 1000) {
          handleRefresh();
          return REFRESH_INTERVAL;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [handleRefresh]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown(prev => Math.max(0, prev - 1000));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldown]);

  return (
    <div className="app-container">
      <Header 
        selectedCollege={selectedCollege}
        onCollegeChange={setSelectedCollege}
        selectedDepartment={selectedDepartment}
        onDepartmentChange={setSelectedDepartment}
        onRefresh={handleRefresh}
        isRefreshing={false} // Logic handled in lists
        refreshCooldown={cooldown}
        autoRefreshTimer={autoRefreshTimer}
        showControls={true} // Explicitly show controls for NoticePage
        mainTitle="my SKKU"
        subTitle="Notice"
        lastRefreshTime={lastRefreshTime}
      />

      {/* Mobile Tabs */}
      <div className="mobile-tabs">
        <button 
          className={`tab-button ${activeTab === 'global' ? 'active' : ''}`}
          onClick={() => setActiveTab('global')}
        >
          학교 전체
        </button>
        <button 
          className={`tab-button ${activeTab === 'college' ? 'active' : ''}`}
          onClick={() => setActiveTab('college')}
        >
          단과대
        </button>
        <button 
          className={`tab-button ${activeTab === 'department' ? 'active' : ''}`}
          onClick={() => setActiveTab('department')}
        >
          학과
        </button>
      </div>

      <main className="main-content">
        <div className={`column ${activeTab === 'global' ? 'mobile-active' : ''}`}>
          <NoticeList 
            title="학교 전체 공지" 
            type="global" 
            refreshTrigger={refreshTrigger}
          />
        </div>
        
        <div className={`column ${activeTab === 'college' ? 'mobile-active' : ''}`}>
          {selectedCollege ? (
            <NoticeList 
              title={`${selectedCollege.name} 공지`} 
              type="college" 
              college={selectedCollege} // Pass the entire object
              refreshTrigger={refreshTrigger}
            />
          ) : (
            <div className="placeholder-message">
              단과대를 선택해주세요.
            </div>
          )}
        </div>

        <div className={`column ${activeTab === 'department' ? 'mobile-active' : ''}`}>
          {selectedDepartment ? (
            <NoticeList 
              title={`${selectedDepartment.name} 공지`} 
              type="department" 
              department={selectedDepartment} // Pass the entire object
              refreshTrigger={refreshTrigger}
            />
          ) : (
            <div className="placeholder-message">
              학과를 선택해주세요.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default NoticePage;
